import type { OrganizationMember } from '@onefootprint/request-types/dashboard';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import useSession from '@/hooks/use-session';

type EmployeeExperimentsStore = {
  experiments: EmployeeExperiment[];
};

export type EmployeeExperiment = (typeof EMPLOYEE_EXPERIMENTS)[number];

export const EMPLOYEE_EXPERIMENTS = ['playbook-builder-debug-mode'] as const;

/** Never on by default — reachable only by turning it on in Toolbox. */
const OPT_IN_ONLY: EmployeeExperiment[] = ['playbook-builder-debug-mode'];

// One day would be nice to have more advanced employee IAM roles from the backend
const SALES_EMAILS = [
  'eli@onefootprint.com',
  'surya@onefootprint.com',
  'geronimo@onefootprint.com',
  'peter@onefootprint.com',
];

const initialState: EmployeeExperimentsStore = {
  experiments: [],
};

export const useEmployeeExperimentsStore = create<EmployeeExperimentsStore>()(
  persist(_ => initialState, {
    name: 'employee-experiments-store',
    version: 1,
  }),
);

const useEmployeeExperiments = () => {
  const data = useEmployeeExperimentsStore();
  const set = useEmployeeExperimentsStore.setState;

  const {
    data: { user },
  } = useSession();

  const isEnabled = (experiment: EmployeeExperiment) => {
    return user?.isFirmEmployee && data?.experiments?.includes(experiment);
  };

  const setExperiments = (experiments: EmployeeExperiment[]) => {
    set({ experiments });
  };

  const setDefaultExperiments = (user?: OrganizationMember) => {
    if (user?.isFirmEmployee && !SALES_EMAILS.includes(user.email)) {
      setExperiments(EMPLOYEE_EXPERIMENTS.filter(experiment => !OPT_IN_ONLY.includes(experiment)));
    } else {
      setExperiments([]);
    }
  };

  return {
    isEnabled,
    setExperiments,
    setDefaultExperiments,
  };
};

export default useEmployeeExperiments;
